import React, { useEffect, useState } from 'react';

import {
  Breakpoints,
  CTA,
  HandleSelectEvent,
  Infobox,
  InputAppearanceValue,
  LoaderSpinner,
  Select,
  TextColor,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextWeight,
  ThemeProvider,
} from '@vw-marketing/us-components';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { NoTranslateTextInput } from '../../../../shared/components/no-translate-text-input';
import {
  onVWBasicStepLoadEvent,
  onVWBasicButtonClickWithProcessEvent,
  onVWBasicLayerLoadEvent,
} from '../../../../shared/tagging/tagging-helpers';

import {
  InfoboxContainer,
  StyledButtonsContainer,
  StyledFormWrapper,
  StyledInputsContainer,
} from './styles';
import { SecurityQuestion, UserSecurityQuestion } from '../../../../shared/typings/main';

export interface EditSecurityQuestionsFormViewProps {
  authService: any;
  translationHandler: (key: string) => string;
  trackingManager: any;
  localeCode: string;
  onSuccess: () => void;
  onChange: () => void;
  questions: SecurityQuestion[];
  userQuestions: UserSecurityQuestion[];
  isCanada: boolean;
}

export const EditSecurityQuestionsFormView = ({
  authService,
  translationHandler,
  trackingManager,
  localeCode,
  onSuccess,
  onChange,
  questions,
  userQuestions,
  isCanada
}: EditSecurityQuestionsFormViewProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    try {
      onVWBasicStepLoadEvent(trackingManager, {
        Name: 'Account Settings',
        StepName: 'Personal Information and Security - Security Questions',
        StepNumber: '4.3',
      });

      if (isCanada) {
        onVWBasicLayerLoadEvent(trackingManager, 'Security - Security Questions');
      }
    } catch (error) {
      console.warn('Error tracking security questions step load:', error);
    }
  }, [trackingManager]);

  const validationSchema = Yup.object({
    firstSecurityQuestion: Yup.string(),
    firstSecurityQuestionAnswer: Yup.string().required(
      translationHandler('accountSettings.edit.questions.answer.error.empty'),
    ),
    secondSecurityQuestion: Yup.string(),
    secondSecurityQuestionAnswer: Yup.string().required(
      translationHandler('accountSettings.edit.questions.answer.error.empty'),
    ),
  });

  const formik = useFormik({
    initialValues: {
      firstSecurityQuestion: String(
        userQuestions?.[0]?.questionId ?? questions[0].questionId
      ),
      firstSecurityQuestionAnswer: userQuestions?.[0]?.questionResponse ?? '',
      secondSecurityQuestion: String(
        userQuestions?.[1]?.questionId ?? questions[1].questionId
      ),
      secondSecurityQuestionAnswer: userQuestions?.[1]?.questionResponse ?? '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        // Track Save button click
        onVWBasicButtonClickWithProcessEvent(trackingManager, {
          Name: 'Account Settings',
          StepName: 'Personal Information and Security - Security Questions',
          StepNumber: '4.3',
        }, { name: 'Save' });
      } catch (e) {
        console.warn('Error tracking Save button click:', e);
      }

      try {
        setIsLoading(true);
        setError(false);
        const { firstSecurityQuestion,
          firstSecurityQuestionAnswer,
          secondSecurityQuestion,
          secondSecurityQuestionAnswer
        } = values;
        const dataToSend: Record<string, any> = {};
        if (firstSecurityQuestion) {
          dataToSend.firstSecurityQuestion = {
            questionId: Number(firstSecurityQuestion),
            questionType:
              questions.find(
                (question) =>
                  question.questionId === Number(firstSecurityQuestion)
              )?.questionType ?? '',
          };
          dataToSend.firstSecurityQuestionAnswer = firstSecurityQuestionAnswer;
        }
        if (secondSecurityQuestion) {
          dataToSend.secondSecurityQuestion = {
            questionId: Number(secondSecurityQuestion),
            questionType:
              questions.find(
                (question) =>
                  question.questionId === Number(secondSecurityQuestion)
              )?.questionType ?? '',
          };

          dataToSend.secondSecurityQuestionAnswer =
            secondSecurityQuestionAnswer;
        }
        await authService.saveSecurityQuestionGqlRequest(dataToSend, localeCode);
        onSuccess();
      } catch (e) {
        console.error(e);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    },
  });

  const getFieldProps = (
    name: string,
  ): {
    name: string;
    onChange: (e: React.SyntheticEvent<HTMLInputElement>) => void;
    onBlur: (e: React.SyntheticEvent<HTMLInputElement>) => void;
    value: any;
    message: string | undefined;
    appearance: InputAppearanceValue | undefined;
  } => ({
    name,
    onChange: (e) => {
      formik.handleChange(e);
    },
    onBlur: formik.handleBlur,
    value: formik.values[name],
    message:
      formik.touched[name] && formik.errors[name]
        ? formik.errors[name]
        : undefined,
    appearance:
      formik.touched[name] && formik.errors[name]
        ? ('error' as InputAppearanceValue)
        : undefined,
  });

  const handleFirstSelectChange: HandleSelectEvent = (e: React.SyntheticEvent<HTMLSelectElement>) => {
    const value = e.currentTarget.value;
    formik.setFieldValue('firstSecurityQuestion', value);
    formik.setFieldTouched('firstSecurityQuestion', true);

    // Force validation to run after value is set
    Promise.resolve().then(() => { formik.validateForm(); });
  };

  const handleSecondSelectChange: HandleSelectEvent = (e: React.SyntheticEvent<HTMLSelectElement>) => {
    const value = e.currentTarget.value;

    formik.setFieldValue('secondSecurityQuestion', value);
    formik.setFieldTouched('secondSecurityQuestion', true);

    // Force validation to run after value is set
    Promise.resolve().then(() => { formik.validateForm(); });
  };

  useEffect(() => {
    if (formik.dirty && onChange) onChange();
  }, [formik.values, onChange]);

  const isButtonDisabled = Object.keys(formik.errors).length > 0 || !formik.dirty || formik.isSubmitting;

  if (isLoading) return <LoaderSpinner />;

  return (
    <ThemeProvider theme="main">
      <StyledFormWrapper>
        <TextCustom
          style={{
            [Breakpoints.default]: {
              fontSize: TextSize.TextSize16,
              color: TextColor.primary,
              fontWeight: TextWeight.light,
              lineHeight: TextLineHeight.TextLineHeight24,
            },
          }}
        >
          {translationHandler('accountSettings.edit.questions.form.description')}
        </TextCustom>
        <form onSubmit={formik.handleSubmit}>
          <StyledInputsContainer className="inputs-container">
            <Select
              // @ts-ignore
              id="firstSecurityQuestion"
              appearance={formik.touched.firstSecurityQuestion && formik.errors.firstSecurityQuestion && formik.values.firstSecurityQuestion === '' ? ('error' as any) : undefined}
              aria-label="Select question"
              isFloating
              aria-required="true"
              required
              message={formik.touched.firstSecurityQuestion && formik.errors.firstSecurityQuestion && formik.values.firstSecurityQuestion === '' ? translationHandler(formik.errors.firstSecurityQuestion) : undefined}
              onBlur={() => { formik.setFieldTouched('firstSecurityQuestion', true); }}
              onChange={handleFirstSelectChange}
              value={formik.values.firstSecurityQuestion}
              name="firstSecurityQuestion"
              label={translationHandler('accountSettings.edit.questions.1st')}
            >
              {questions.map((item) => {
                const isDisabled =
                  item.questionId ===
                  Number(formik.values.secondSecurityQuestion); // Check if this option is selected in the other select
                return (
                  <option
                    key={`question-option-${item.questionId}`}
                    value={item.questionId}
                    disabled={isDisabled} // Disable the option if selected in the other select
                  >
                    {item.questionText}
                  </option>
                );
              })}
            </Select>
            <NoTranslateTextInput
              {...getFieldProps('firstSecurityQuestionAnswer')}
              aria-label={translationHandler('accountSettings.edit.questions.answer')}
              aria-required="true"
              required
              label={translationHandler('accountSettings.edit.questions.answer')}
            />
            <Select
              // @ts-ignore
              id="secondSecurityQuestion"
              appearance={formik.touched.secondSecurityQuestion && formik.errors.secondSecurityQuestion && formik.values.secondSecurityQuestion === '' ? ('error' as any) : undefined}
              aria-label="Select question"
              aria-required="true"
              required
              isFloating
              message={formik.touched.secondSecurityQuestion && formik.errors.secondSecurityQuestion && formik.values.secondSecurityQuestion === '' ? translationHandler(formik.errors.secondSecurityQuestion) : undefined}
              onBlur={() => { formik.setFieldTouched('secondSecurityQuestion', true); }}
              onChange={handleSecondSelectChange}
              value={formik.values.secondSecurityQuestion}
              name="secondSecurityQuestion"
              label={translationHandler('accountSettings.edit.questions.2nd')}
            >
              {questions.map((item) => {
                const isDisabled =
                  item.questionId === Number(formik.values.firstSecurityQuestion); // Check if this option is selected in the other select
                return (
                  <option
                    key={`question-option-${item.questionId}`}
                    value={item.questionId}
                    disabled={isDisabled} // Disable the option if selected in the other select
                  >
                    {item.questionText}
                  </option>
                );
              })}
            </Select>
            <NoTranslateTextInput
              {...getFieldProps('secondSecurityQuestionAnswer')}
              aria-label={translationHandler('accountSettings.edit.questions.answer')}
              aria-required="true"
              required
              label={translationHandler('accountSettings.edit.questions.answer')}
            />
          </StyledInputsContainer>
          {error && (
            <InfoboxContainer>
              <Infobox>{translationHandler('accountSettings.edit.questions.error')}</Infobox>
            </InfoboxContainer>
          )}
          <StyledButtonsContainer>
            <CTA stretchContent aria-label="Save button" disabled={isButtonDisabled} emphasis="primary" tag="button" type="submit">
              {translationHandler('accountSettings.edit.questions.form.button')}
            </CTA>
          </StyledButtonsContainer>
        </form>
      </StyledFormWrapper>
    </ThemeProvider>
  );
};